'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { X } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/utils';

// ---- Dialog -------------------------------------------------------------------

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export function DialogContent({ title, description, children, className, wide }: { title: ReactNode; description?: ReactNode; children: ReactNode; className?: string; wide?: boolean }) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
      <DialogPrimitive.Content
        className={cn(
          'fixed left-1/2 top-1/2 z-50 max-h-[90dvh] w-[calc(100vw-24px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[14px] border border-border bg-panel p-5 shadow-2xl shadow-black/40 focus:outline-none data-[state=open]:animate-pop',
          wide ? 'max-w-2xl' : 'max-w-md',
          className,
        )}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <DialogPrimitive.Title className="text-base font-semibold text-fg">{title}</DialogPrimitive.Title>
            {description ? <DialogPrimitive.Description className="mt-1 text-sm text-fg-2">{description}</DialogPrimitive.Description> : <DialogPrimitive.Description className="sr-only">{String(title)}</DialogPrimitive.Description>}
          </div>
          <DialogPrimitive.Close className="rounded-md p-1 text-fg-3 hover:bg-hover hover:text-fg" aria-label="Fechar">
            <X className="size-4" />
          </DialogPrimitive.Close>
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

// ---- Sheet (side / bottom drawer) ---------------------------------------------

export function SheetContent({ title, children, side = 'right', className }: { title: ReactNode; children: ReactNode; side?: 'right' | 'bottom' | 'left'; className?: string }) {
  const pos = {
    right: 'right-0 top-0 h-dvh w-full max-w-xl border-l',
    left: 'left-0 top-0 h-dvh w-72 border-r',
    bottom: 'bottom-0 left-0 max-h-[88dvh] w-full rounded-t-[16px] border-t',
  }[side];
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-fade-in" />
      <DialogPrimitive.Content className={cn('fixed z-50 flex flex-col overflow-hidden border-border bg-panel shadow-2xl focus:outline-none data-[state=open]:animate-slide-up', pos, className)}>
        <div className="flex items-center justify-between border-b border-border-soft px-5 py-4">
          <DialogPrimitive.Title className="text-[15px] font-semibold">{title}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">{String(title)}</DialogPrimitive.Description>
          <DialogPrimitive.Close className="rounded-md p-1 text-fg-3 hover:bg-hover hover:text-fg" aria-label="Fechar">
            <X className="size-4" />
          </DialogPrimitive.Close>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

// ---- Dropdown -----------------------------------------------------------------

export const DropdownMenu = DropdownPrimitive.Root;
export const DropdownMenuTrigger = DropdownPrimitive.Trigger;

export function DropdownMenuContent({ className, align = 'end', ...p }: ComponentProps<typeof DropdownPrimitive.Content>) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        align={align}
        sideOffset={6}
        className={cn('z-50 min-w-48 rounded-[12px] border border-border bg-panel p-1 shadow-xl shadow-black/40 data-[state=open]:animate-fade-in', className)}
        {...p}
      />
    </DropdownPrimitive.Portal>
  );
}

export function DropdownMenuItem({ className, ...p }: ComponentProps<typeof DropdownPrimitive.Item>) {
  return (
    <DropdownPrimitive.Item
      className={cn('flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-fg-2 outline-none data-[highlighted]:bg-hover data-[highlighted]:text-fg [&_svg]:size-4', className)}
      {...p}
    />
  );
}

export function DropdownMenuLabel({ className, ...p }: ComponentProps<typeof DropdownPrimitive.Label>) {
  return <DropdownPrimitive.Label className={cn('px-2.5 py-1.5 text-[11px] font-medium uppercase tracking-wider text-fg-3', className)} {...p} />;
}

export const DropdownMenuSeparator = () => <DropdownPrimitive.Separator className="my-1 h-px bg-border-soft" />;

// ---- Popover ------------------------------------------------------------------

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export function PopoverContent({ className, align = 'end', ...p }: ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={8}
        className={cn('z-50 w-80 rounded-[12px] border border-border bg-panel shadow-xl shadow-black/40 focus:outline-none data-[state=open]:animate-fade-in', className)}
        {...p}
      />
    </PopoverPrimitive.Portal>
  );
}

// ---- Tooltip ------------------------------------------------------------------

export const TooltipProvider = TooltipPrimitive.Provider;

export function Tip({ content, children, side = 'top' }: { content: ReactNode; children: ReactNode; side?: 'top' | 'right' | 'bottom' | 'left' }) {
  return (
    <TooltipPrimitive.Root delayDuration={200}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content side={side} sideOffset={6} className="z-50 max-w-64 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-fg shadow-lg data-[state=delayed-open]:animate-fade-in">
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

// ---- Switch -------------------------------------------------------------------

export function Switch({ className, ...p }: ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      className={cn('relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-border bg-card transition-colors data-[state=checked]:border-lime/40 data-[state=checked]:bg-lime-soft disabled:opacity-50', className)}
      {...p}
    >
      <SwitchPrimitive.Thumb className="block size-3.5 translate-x-0.5 rounded-full bg-fg-3 transition-transform duration-150 data-[state=checked]:translate-x-[18px] data-[state=checked]:bg-lime" />
    </SwitchPrimitive.Root>
  );
}

// ---- Tabs ---------------------------------------------------------------------

export const Tabs = TabsPrimitive.Root;
export const TabsContent = TabsPrimitive.Content;

export function TabsList({ className, ...p }: ComponentProps<typeof TabsPrimitive.List>) {
  return <TabsPrimitive.List className={cn('inline-flex items-center gap-1 rounded-[11px] border border-border-soft bg-bg-2 p-1', className)} {...p} />;
}

export function TabsTrigger({ className, ...p }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-[8px] px-3 text-[13px] font-medium text-fg-3 transition-colors hover:text-fg data-[state=active]:bg-card data-[state=active]:text-fg data-[state=active]:shadow-sm [&_svg]:size-3.5',
        className,
      )}
      {...p}
    />
  );
}
